import { getGroupAnchorIndexId, snapshotGroupMemberMarketing } from '../quotation/quotationGroupingHelpers'
import {
  getCatalogProductWithAdjustment,
  hydrateSavedOrderLinePrices,
  parseMarketingAdjValueTypeFromDb,
} from 'src/utils/retailPriceEdit'

export const getCommunityLocationFromProject = (community) => {
  if (!community) {
    return { address: '', city: '', state: '', zip: '' }
  }
  return {
    address: (community.address || '').toString().trim(),
    city: (community.city || '').toString().trim(),
    state: (community.state || '').toString().trim(),
    zip: (community.zip || community.zipcode || '').toString().trim(),
  }
}

export const hasCompleteProductLocation = (product) => {
  const locations = Array.isArray(product?.location) ? product.location : []
  const firstLocation = locations[0]
  return Boolean(
    firstLocation?.address &&
      firstLocation?.city &&
      firstLocation?.state &&
      firstLocation?.zip,
  )
}

export const withCommunityLocationIfMissing = (product, community) => {
  if (!product || hasCompleteProductLocation(product)) return product
  const communityLocation = getCommunityLocationFromProject(community)
  if (
    !communityLocation.address ||
    !communityLocation.city ||
    !communityLocation.state ||
    !communityLocation.zip
  ) {
    return product
  }
  return {
    ...product,
    location: [{ id: 0, ...communityLocation }],
  }
}

const parseDesignOrderProductLocation = (product) => {
  try {
    const loc = product?.location
      ? typeof product.location === 'string'
        ? JSON.parse(product.location)
        : product.location
      : []
    return Array.isArray(loc) && loc.length > 0
      ? loc
      : [{ address: product?.location || '', city: '', state: '', zip: '' }]
  } catch (e) {
    return [{ address: product?.location || '', city: '', state: '', zip: '' }]
  }
}

export const createEmptyDesignOrderProductRow = (indexId = 1) => ({
  indexId,
  entryMode: 'write',
  product_name: '',
  product_desc: '',
  price: 0,
  retail_price: 0,
  is_product_row: 1,
  catalogProduct: null,
  linkedCatalog: null,
  is_existing_catalog: 0,
  isPersistedRow: false,
})

export const isDesignOrderCatalogLine = (product) => {
  if (product?.is_product_row === 1 || product?.is_product_row === '1') return false
  if (product?.is_product_row === 0 || product?.is_product_row === '0') return true
  return product?.product_id != null && Number(product.product_id) > 0
}

export const isOnlyEmptyDesignOrderProductRow = (rows) =>
  Array.isArray(rows) &&
  rows.length === 1 &&
  rows[0]?.entryMode === 'write' &&
  !String(rows[0]?.product_name || '').trim() &&
  !rows[0]?.catalogProduct &&
  !rows[0]?.linkedCatalog

const isBlankDesignOrderWriteRow = (row) =>
  (row?.is_product_row === 1 || row?.is_product_row === '1') &&
  row?.entryMode === 'write' &&
  !String(row?.product_name || '').trim() &&
  !String(row?.product_desc || '').trim() &&
  !row?.linkedCatalog &&
  !row?.catalogProduct &&
  !row?.isPersistedRow

const hasPersistedDesignOrderWriteProducts = (productsData) =>
  (productsData || []).some(
    (product) =>
      !isDesignOrderCatalogLine(product) &&
      String(product?.name || product?.product_name || '').trim() !== '',
  )

/** True when real write-in products exist (not the empty edit-form placeholder row). */
export const hasDesignOrderWriteProductsBlockingConvert = (rows, persistedProducts) => {
  if (isOnlyEmptyDesignOrderProductRow(rows)) {
    return hasPersistedDesignOrderWriteProducts(persistedProducts)
  }
  const hasUiWrite = (rows || []).some(
    (row) =>
      (row?.is_product_row === 1 || row?.is_product_row === '1') &&
      !isBlankDesignOrderWriteRow(row),
  )
  return hasUiWrite || hasPersistedDesignOrderWriteProducts(persistedProducts)
}

const buildLinkedCatalogLineFromApiProduct = (product, indexId, groupMetaById, projectCommunity) => {
  const hydratedPrices = hydrateSavedOrderLinePrices(product, {}, { retailSource: 'design_order' })
  const productGroupId = product?.group_id || null
  const groupMeta =
    productGroupId && groupMetaById
      ? groupMetaById.get(String(productGroupId)) || {}
      : {}
  return withCommunityLocationIfMissing(
    {
      ...product,
      ...hydratedPrices,
      indexId,
      product_id: product?.product_id ?? null,
      id: product?.product_id ?? null,
      group_id: productGroupId,
      group_name: groupMeta?.group_name || product?.group_name || null,
      group_description: groupMeta?.group_description || product?.group_description || null,
      group_product_quantity:
        product?.group_product_quantity ??
        product?.locationQuantity ??
        product?.location_quantity ??
        product?.quantity ??
        1,
      group_install_quantity:
        product?.group_install_quantity ??
        product?.install_quantity ??
        1,
      allLocationSame:
        groupMeta?.group_all_location_same === 1 ||
        product?.allLocationSame === 1 ||
        product?.allLocationSame === true,
      is_single_card:
        groupMeta?.group_is_single_card === 1 ||
        product?.is_single_card === 1 ||
        product?.is_single_card === true,
      ...(productGroupId
        ? snapshotGroupMemberMarketing({
            adjustment: product?.adjustment,
            adj_value_type: product?.adj_value_type,
            install_adjustment: product?.install_adjustment,
            install_adj_value_type: product?.install_adj_value_type,
          })
        : {}),
      name: product?.name || product?.product_name || '',
      description: product?.description || product?.product_desc || '',
      product_desc: product?.description || product?.product_desc || '',
      product_notes: product?.product_notes || '',
      install_notes: product?.install_notes || '',
      install_quantity: product?.install_quantity || 1,
      quantity: product?.quantity || product?.locationQuantity || product?.location_quantity || 1,
      locationQuantity:
        product?.locationQuantity || product?.location_quantity || product?.quantity || 1,
      product_sub_total: product?.product_sub_total ?? null,
      product_total: product?.product_total ?? null,
      expected_formula_price:
        product?.expected_formula_price || product?.expected_cost || product?.unit_cost || 0,
      unit_cost: product?.unit_cost || product?.expected_formula_price || product?.expected_cost || 0,
      markup: product?.markup || product?.product_markup || 0,
      adjustment: product?.adjustment ?? 0,
      adj_value_type: parseMarketingAdjValueTypeFromDb(product?.adj_value_type),
      markAdjType: product?.markAdjType ?? 2,
      install_adjustment: product?.install_adjustment ?? 0,
      install_adj_value_type: parseMarketingAdjValueTypeFromDb(product?.install_adj_value_type),
      retail_price_edited: true,
      install_price_edited: Boolean(hydratedPrices.original_install_cost),
      location: parseDesignOrderProductLocation(product),
      is_product_row: 0,
    },
    projectCommunity,
  )
}

/** Map API productsData → productRows for Design Order / Edit Design Job. */
export const buildDesignOrderProductRowsFromApi = (
  productsData,
  groupsFromApi = [],
  projectCommunity = null,
) => {
  const sorted = [...(productsData || [])].sort(
    (a, b) =>
      (Number(a?.product_index) || 0) - (Number(b?.product_index) || 0) ||
      (Number(a?.id) || 0) - (Number(b?.id) || 0),
  )

  if (sorted.length === 0) {
    return {
      mappedRows: [createEmptyDesignOrderProductRow(1)],
      productAttachmentsByIndex: {},
      indexCounter: 2,
    }
  }

  const groupMetaById = new Map(
    (groupsFromApi || []).map((group) => [
      String(group?.id ?? group?.group_id),
      group,
    ]),
  )
  const productAttachmentsByIndex = {}

  const mappedRows = sorted.map((product, index) => {
    const lineIndex = index + 1
    productAttachmentsByIndex[lineIndex] = Array.isArray(product.attachments)
      ? product.attachments
      : []

    if (isDesignOrderCatalogLine(product)) {
      const hydratedPrices = hydrateSavedOrderLinePrices(
        product,
        {},
        { retailSource: 'design_order' },
      )
      const catalogProductId = product?.product_id ?? product?.id ?? null
      const productGroupId = product?.group_id || null
      const groupMeta = productGroupId
        ? groupMetaById.get(String(productGroupId)) || {}
        : {}
      const catalogLine = withCommunityLocationIfMissing(
        {
          ...product,
          ...hydratedPrices,
          indexId: lineIndex,
          product_id: catalogProductId,
          id: catalogProductId,
          name: product?.name || product?.product_name || '',
          description: product?.description || product?.product_desc || '',
          product_desc: product?.description || product?.product_desc || '',
          product_notes: product?.product_notes || '',
          install_notes: product?.install_notes || '',
          install_quantity: product?.install_quantity || 1,
          quantity:
            product?.quantity ||
            product?.locationQuantity ||
            product?.location_quantity ||
            1,
          locationQuantity:
            product?.locationQuantity ||
            product?.location_quantity ||
            product?.quantity ||
            1,
          product_sub_total: product?.product_sub_total ?? null,
          product_total: product?.product_total ?? null,
          group_id: productGroupId,
          group_name: groupMeta?.group_name || product?.group_name || null,
          group_description:
            groupMeta?.group_description || product?.group_description || null,
          group_product_quantity:
            product?.group_product_quantity ??
            product?.locationQuantity ??
            product?.location_quantity ??
            product?.quantity ??
            1,
          group_install_quantity:
            product?.group_install_quantity ??
            product?.install_quantity ??
            1,
          allLocationSame:
            groupMeta?.group_all_location_same === 1 ||
            product?.allLocationSame === 1 ||
            product?.allLocationSame === true,
          is_single_card:
            groupMeta?.group_is_single_card === 1 ||
            product?.is_single_card === 1 ||
            product?.is_single_card === true,
          ...(productGroupId
            ? snapshotGroupMemberMarketing({
                adjustment: product?.adjustment,
                adj_value_type: product?.adj_value_type,
                install_adjustment: product?.install_adjustment,
                install_adj_value_type: product?.install_adj_value_type,
              })
            : {}),
          expected_formula_price:
            product?.expected_formula_price ||
            product?.expected_cost ||
            product?.unit_cost ||
            0,
          unit_cost:
            product?.unit_cost ||
            product?.expected_formula_price ||
            product?.expected_cost ||
            0,
          markup: product?.markup || product?.product_markup || 0,
          adjustment: product?.adjustment ?? 0,
          adj_value_type: parseMarketingAdjValueTypeFromDb(product?.adj_value_type),
          markAdjType: product?.markAdjType ?? 2,
          install_adjustment: product?.install_adjustment ?? 0,
          install_adj_value_type: parseMarketingAdjValueTypeFromDb(
            product?.install_adj_value_type,
          ),
          install_formula_cost_each:
            product?.install_formula_cost_each != null &&
              product?.install_formula_cost_each !== ''
              ? Number(product.install_formula_cost_each)
              : Number(product?.expected_install_labor_cost_with_markup || 0),
          retail_price_edited: true,
          install_price_edited: Boolean(hydratedPrices.original_install_cost),
          location: parseDesignOrderProductLocation(product),
          is_product_row: 0,
        },
        projectCommunity,
      )
      return {
        indexId: lineIndex,
        entryMode: 'choose',
        product_name: catalogLine.name,
        product_desc: catalogLine.description,
        price: 0,
        retail_price: 0,
        is_product_row: 0,
        product_write: Number(product?.product_write) === 1 ? 1 : 0,
        catalogProduct: catalogLine,
        isPersistedRow: Boolean(product?.id),
        designOrderProductId: product?.id ?? null,
        addedByUserType: product?.addedByUserType ?? product?.added_by_user_type ?? null,
      }
    }

    let linkedCatalog = null
    let payload = null
    try {
      payload = product?.product_payload
        ? typeof product.product_payload === 'string'
          ? JSON.parse(product.product_payload)
          : product.product_payload
        : null
    } catch (e) {
      payload = null
    }
    const linkedCatalogId =
      product?.catalogId ||
      product?.product_id ||
      payload?.catalogId ||
      payload?.product_id ||
      null
    const hasLinkedCatalog = Boolean(
      linkedCatalogId &&
        (product?.has_linked_catalog == 1 ||
          payload?.has_linked_catalog == 1 ||
          product?.is_existing_catalog == 1 ||
          payload?.is_existing_catalog == 1),
    )
    if (hasLinkedCatalog) {
      linkedCatalog = buildLinkedCatalogLineFromApiProduct(
        product,
        lineIndex,
        groupMetaById,
        projectCommunity,
      )
    }

    return {
      indexId: lineIndex,
      entryMode: 'write',
      product_name: product?.name || product?.product_name || '',
      product_desc: product?.description || product?.product_desc || '',
      product_notes: product?.product_notes || '',
      install_notes: product?.install_notes || '',
      price: 0,
      retail_price: 0,
      is_product_row: 1,
      product_write: Number(product?.product_write) === 1 ? 1 : 0,
      catalogProduct: null,
      linkedCatalog,
      is_existing_catalog: product?.is_existing_catalog ?? 0,
      product_id: linkedCatalog?.product_id || linkedCatalog?.id || null,
      isPersistedRow: Boolean(product?.id),
      designOrderProductId: product?.id ?? null,
      addedByUserType: product?.addedByUserType ?? product?.added_by_user_type ?? null,
    }
  })

  return {
    mappedRows,
    productAttachmentsByIndex,
    indexCounter: mappedRows.length + 1,
  }
}

/** Catalog lines from design-order product rows (flat shape for grouping hook). */
export const getCatalogProductsFromRows = (rows) =>
  (rows || [])
    .filter((row) => row?.entryMode === 'choose' && row?.catalogProduct)
    .map((row) => ({
      ...row.catalogProduct,
      indexId: row.indexId,
      entryMode: 'choose',
      is_product_row: 0,
      locationQuantity:
        row.catalogProduct.locationQuantity ??
        row.catalogProduct.quantity ??
        1,
      quantity:
        row.catalogProduct.quantity ??
        row.catalogProduct.locationQuantity ??
        1,
      install_quantity: row.catalogProduct.install_quantity ?? 1,
    }))

/** Write grouped catalog fields back onto productRows.catalogProduct. */
export const syncCatalogProductsToRows = (rows, catalogProducts) => {
  const byIndexId = new Map(
    (catalogProducts || []).map((product) => [String(product.indexId), product]),
  )
  return (rows || []).map((row) => {
    if (row?.entryMode !== 'choose' || !row?.catalogProduct) return row
    const updated = byIndexId.get(String(row.indexId))
    if (!updated) return row
    return {
      ...row,
      catalogProduct: { ...updated, indexId: row.indexId },
      product_name: updated.name || updated.product_name || row.product_name || '',
      product_desc:
        updated.description ||
        updated.product_desc ||
        row.product_desc ||
        '',
    }
  })
}

/** Interleave write rows, ungrouped catalog rows, and group blocks. */
export const computeDesignOrderProductTableRenderOrder = (productRows, groupedProducts) => {
  const groupMap = new Map((groupedProducts || []).map((g) => [String(g.id), g]))
  const sorted = [...(productRows || [])].sort(
    (a, b) => (Number(a?.indexId) || 0) - (Number(b?.indexId) || 0),
  )
  const out = []
  const seenGroups = new Set()

  for (const row of sorted) {
    if (!row?.indexId) continue

    if (row?.entryMode !== 'choose' || !row?.catalogProduct) {
      out.push({ type: 'row', indexId: row.indexId })
      continue
    }

    const gid = row.catalogProduct?.group_id
      ? String(row.catalogProduct.group_id)
      : null

    if (!gid) {
      out.push({ type: 'row', indexId: row.indexId })
      continue
    }

    if (seenGroups.has(gid)) continue

    const group = groupMap.get(gid)
    if (!group) {
      out.push({ type: 'row', indexId: row.indexId })
      continue
    }

    const anchorId = getGroupAnchorIndexId(group)
    if (anchorId == null) {
      out.push({ type: 'row', indexId: row.indexId })
      continue
    }

    if (Number(row.indexId) !== Number(anchorId)) continue

    seenGroups.add(gid)
    out.push({ type: 'group', groupId: gid, indexId: anchorId })
  }

  return out
}

/** Sum ungrouped catalog lines + grouped block totals (includes marketing adjustments). */
export const computeDesignOrderProductsSubTotal = (productRows, groupedProducts) => {
  const ungroupedTotal = (productRows || []).reduce((total, row) => {
    if (row?.entryMode !== 'choose' || !row?.catalogProduct) return total
    const gid = row.catalogProduct?.group_id
    if (gid != null && String(gid) !== '') return total

    const data = getCatalogProductWithAdjustment(row.catalogProduct)
    if (!data) return total

    const retailPrice = Number(data.actual_retail_price || data.retail_price || 0)
    const qty = Number(data.quantity || data.locationQuantity || 1)
    const installCost = parseFloat(data?.install_cost) || 0
    const installQty = Number(data.install_quantity || 1)
    const productSubTotal = retailPrice * qty
    const installSubTotal = installCost > 0 ? installCost * installQty : 0
    return total + productSubTotal + installSubTotal
  }, 0)

  const groupTotal = (groupedProducts || []).reduce(
    (total, group) => total + (Number(group.adjustedGroupTotal) || 0),
    0,
  )

  return ungroupedTotal + groupTotal
}

export const DESIGN_ORDER_NEGATIVE_MARKETING_PERMISSIONS = [
  'Allow Negative Marketing Adjustment Design',
]

export const getDesignOrderLosingMoneyRowSx = (theme, isLosingMoneyItem) =>
  isLosingMoneyItem
    ? {
        '& .MuiTableCell-root, & .MuiTableCell-root .MuiInputBase-input, & .MuiTableCell-root .MuiFormLabel-root, & .MuiTableCell-root .MuiTypography-root':
          {
            color: `${theme.palette.error.main} !important`,
          },
      }
    : undefined

export const DESIGN_ORDER_PRODUCT_TABLE_PRODUCT_COL_SX = {
  minWidth: 280,
  width: 280,
  maxWidth: 280,
  verticalAlign: 'middle',
}

export const DESIGN_ORDER_PRODUCT_TABLE_DESCRIPTION_COL_SX = {
  minWidth: 320,
  width: 320,
  maxWidth: 320,
  verticalAlign: 'middle',
}

export const DESIGN_ORDER_ROW_ACTIONS_CELL_SX = {
  width: 112,
  minWidth: 112,
  maxWidth: 112,
  px: 0.5,
  verticalAlign: 'middle',
}

export const DESIGN_ORDER_ROW_ACTIONS_BOX_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  gap: 0.5,
}

export const DESIGN_ORDER_ROW_ACTIONS_LEADING_SLOT_SX = {
  width: 72,
  minWidth: 72,
  height: 40,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const resolveDesignOrderMarkupType = (markupType) => Number(markupType ?? 2)

export const getDesignOrderGroupValueColumns = (markupType, hasMarkupPermission) => [
  { id: 1, value: 'Sl No.' },
  { id: 24, value: 'Mode' },
  { id: 2, value: 'Products' },
  { id: 25, value: 'Description' },
  { id: 3, value: 'Notes' },
  { id: 19, value: 'Attachments' },
  { id: 4, value: 'Product formula cost Each' },
  { id: 20, value: 'Markup (%)' },
  {
    id: 5,
    value: 'Retail Price Each',
  },
  {
    id: 6,
    value: null,
  },
  { id: 9, value: 'Qty' },
  { id: 10, value: 'Location' },
  { id: 11, value: 'Marketing Adjustment Type' },
  { id: 15, value: 'Marketing Adjustment' },
  { id: 12, value: 'Subtotal' },
  { id: 14, value: 'Product Total' },
  { id: 13, value: '' },
]

export const resolveDesignOrderGroupDisplay = (row, groupedProductsMap) => {
  const groupId = row?.catalogProduct?.group_id ?? row?.group_id ?? null
  if (!groupId) return null
  const group = groupedProductsMap?.get(String(groupId))
  const groupName =
    group?.name ||
    group?.group_name ||
    row?.catalogProduct?.group_name ||
    row?.group_name ||
    'Group'
  return { groupId, groupName }
}

export const mapDesignOrderProductAttachmentForDisplay = (file, row, groupedProductsMap) => {
  const groupDisplay = resolveDesignOrderGroupDisplay(row, groupedProductsMap)
  const productName =
    row?.catalogProduct?.name ||
    row?.catalogProduct?.product_name ||
    row?.linkedCatalog?.name ||
    row?.linkedCatalog?.product_name ||
    row?.product_name ||
    `Product ${row?.indexId}`

  const base = {
    ...file,
    url: file.url || file.name || file.file_url,
    name: file.name || file.url || file.file_url,
    location_ids: file.location_ids || String(row.indexId),
    _productAttachment: true,
    product_index_id: file.product_index_id ?? row.indexId,
  }

  if (groupDisplay) {
    return {
      ...base,
      productNames: [{ product_id: groupDisplay.groupId, product_name: groupDisplay.groupName }],
      _groupAttachment: true,
    }
  }

  return {
    ...base,
    productNames:
      file.productNames ||
      (productName
        ? [
            {
              product_id:
                row.catalogProduct?.product_id ||
                row.catalogProduct?.id ||
                row.linkedCatalog?.product_id ||
                row.linkedCatalog?.id ||
                row.indexId,
              product_name: productName,
            },
          ]
        : []),
    _groupAttachment: false,
  }
}

export const enrichDesignOrderAttachmentForDisplay = (
  file,
  productIndexId,
  productRows,
  groupedProductsMap,
) => {
  const row = (productRows || []).find((p) => Number(p?.indexId) === Number(productIndexId))
  if (!row) return file
  return mapDesignOrderProductAttachmentForDisplay(file, row, groupedProductsMap)
}
